import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  FindAllTreatmentAgenciesPayload,
  TreatmentAgenciesService,
  TreatmentAgencyPayload,
} from "../services/treatmentAgencies.service";

const TREATMENT_AGENCIES_LIST_KEY = "pret-treatment-agencies-list";
const TREATMENT_AGENCY_BY_ID_KEY = "pret-treatment-agency-by-id";

export const useTreatmentAgencies = (
  filters: FindAllTreatmentAgenciesPayload
) => {
  const {
    data: treatmentAgencies,
    isLoading: isLoadingTreatmentAgencies,
    isFetching: isFetchingTreatmentAgencies,
    refetch: refetchTreatmentAgencies,
  } = useQuery({
    queryKey: [TREATMENT_AGENCIES_LIST_KEY, filters],
    queryFn: () => TreatmentAgenciesService.findAll(filters),
  });

  return {
    treatmentAgencies,
    isLoadingTreatmentAgencies,
    isFetchingTreatmentAgencies,
    refetchTreatmentAgencies,
  };
};

export const useTreatmentAgency = (id: string, enabled = true) => {
  const {
    data: treatmentAgency,
    isLoading: isLoadingTreatmentAgency,
    refetch: refetchTreatmentAgency,
  } = useQuery({
    queryKey: [TREATMENT_AGENCY_BY_ID_KEY, id],
    queryFn: () => TreatmentAgenciesService.findOne(id),
    enabled: !!id && enabled,
  });

  return {
    treatmentAgency,
    isLoadingTreatmentAgency,
    refetchTreatmentAgency,
  };
};

export const useCreateTreatmentAgency = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: TreatmentAgencyPayload) =>
      TreatmentAgenciesService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [TREATMENT_AGENCIES_LIST_KEY],
      });
    },
  });
};

export const useUpdateTreatmentAgency = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: TreatmentAgencyPayload }) =>
      TreatmentAgenciesService.update({ id, data }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [TREATMENT_AGENCIES_LIST_KEY],
      });
      queryClient.invalidateQueries({
        queryKey: [TREATMENT_AGENCY_BY_ID_KEY, variables.id],
      });
    },
  });
};

export const useArchiveTreatmentAgency = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => TreatmentAgenciesService.archive(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [TREATMENT_AGENCIES_LIST_KEY],
      });
    },
  });
};
